/**
 * Règle d'accès d'un abonnement — SOURCE UNIQUE, partagée serveur + écrans.
 *
 * Avant le 29/09/2026, l'accès se décidait partout par `status IN ('live','trial')`. Un client
 * qui résiliait perdait l'accès le jour même, alors qu'il avait payé jusqu'à une date future,
 * et l'écran « Licences et Packs » faisait disparaître sa carte d'abonnement sans explication.
 *
 * La règle retenue (arbitrage Valérie, 29/09/2026) est la même pour tous les motifs de sortie —
 * résiliation, non-renouvellement, impayé, suspension, PAUSE :
 *
 *   l'accès reste ouvert tant que la période DÉJÀ PAYÉE n'est pas écoulée.
 *
 * Un abonnement actif ou en essai reste ouvert sans condition de date : sa fin de terme avance
 * à chaque renouvellement, et un renouvellement manqué (cf. clé anti-doublon du webhook) ne doit
 * jamais couper un client qui paie.
 *
 * ⚠️ Ne JAMAIS réécrire ce test en ligne dans un handler ou un écran : le dépôt portait cinq
 * copies divergentes du même genre de règle pour les campagnes, et c'est ce qui les a fait
 * diverger. Appeler `subscriptionAccess()`.
 */

/** Statuts Zoho pour lesquels l'accès est ouvert sans condition de date. */
const STATUTS_ACTIFS = new Set(["live", "trial"]);

/** Statuts Zoho qui ferment l'accès immédiatement : la période payée est déjà consommée. */
const STATUTS_TERMINES = new Set(["expired", "trial_expired", "creation_failed"]);

/**
 * Statuts de sortie qui laissent courir la période payée. Un statut hors de cette liste et hors
 * des deux précédentes (statut inconnu, `future`) ferme l'accès : on ne devine jamais un droit.
 * ⚠️ Le webhook, lui, écrit ENCORE `sub.status || "live"` (`zoho-webhook.ts`, 3 endroits) : un
 * statut VIDE est donc enregistré « live » et reste ouvert. Cette règle ne rattrape que les
 * statuts inconnus effectivement stockés, pas cette requalification à l'écriture.
 */
const STATUTS_SORTIE = new Set([
  "cancelled",
  "cancelled_from_dunning",
  "non_renewing",
  "unpaid",
  "dunning",
  // `suspended` n'est pas un statut de l'API Zoho, mais `deduceEventType` le traite : il peut
  // donc être stocké. L'arbitrage du 29/09 place la suspension avec l'impayé — période payée
  // conservée.
  "suspended",
]);

export type SubscriptionState =
  | "active" // abonnement en cours
  | "trial" // essai en cours
  | "cancelled" // résilié — accès jusqu'à la fin de la période payée
  | "non_renewing" // résiliation programmée en fin de terme
  | "unpaid" // impayé / relance
  | "paused" // en pause
  | "ended"; // période payée écoulée

/** Vue minimale d'un abonnement : ce que la règle a besoin de lire, rien de plus. */
export interface SubscriptionLike {
  status?: string | null;
  currentTermEndsAt?: Date | string | null;
  nextBillingAt?: Date | string | null;
  pauseDate?: Date | string | null;
  resumeDate?: Date | string | null;
  // Essai en cours : reconnu par CES DEUX champs, jamais par le statut (cf. `estUnEssaiEnCours`).
  trialSessionsLimit?: number | null;
  trialDecisionOutcome?: string | null;
}

/**
 * Un essai BizCoach en cours.
 *
 * ⚠️ NE PAS le reconnaître au statut : chez Zoho, l'essai est annulé dès sa création
 * (`cancel_at_end=true`), donc sa fiche porte `non_renewing` — le statut nominal d'un essai en
 * cours (`spec/trial-mode-with-coupon.md`). Sans cette lecture, la réconciliation quotidienne
 * écrasait le statut local « trial » et CHAQUE prospect en essai voyait « Résilié — accès
 * jusqu'au … » le lendemain, sur le tunnel d'acquisition. C'est la lecture qu'emploie déjà
 * `loadUserAndActiveTrial`.
 */
export function estUnEssaiEnCours(sub: SubscriptionLike | null | undefined): boolean {
  return !!sub && sub.trialSessionsLimit != null && !sub.trialDecisionOutcome;
}

export interface SubscriptionAccess {
  /** L'abonnement ouvre-t-il l'accès aux simulations ? */
  isOpen: boolean;
  /** État à afficher (badge, message de blocage). */
  state: SubscriptionState;
  /** Date de fin d'accès, quand elle est connue et que l'accès court encore. */
  accessEndsAt: Date | null;
  /** Date de reprise prévue, pour une pause à terme défini. */
  resumesAt: Date | null;
  /** L'abonnement est-il en pause à la date évaluée ? */
  isPaused: boolean;
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Fin de la période payée. `current_term_ends_at` est la valeur de référence ; `next_billing_at`
 * sert de repli, car le modèle de webhook configuré chez Zoho envoie la seconde et pas toujours
 * la première (constaté sur les payloads reçus en production : 1 sur 6 portait la fin de terme).
 */
export function paidPeriodEnd(sub: SubscriptionLike | null | undefined): Date | null {
  if (!sub) return null;
  return toDate(sub.currentTermEndsAt) ?? toDate(sub.nextBillingAt);
}

/**
 * Une pause est active si sa date de début est passée et qu'aucune reprise n'est déjà survenue.
 * Une pause sans date de reprise est une pause à durée indéterminée.
 */
export function isPauseActive(sub: SubscriptionLike | null | undefined, now: Date = new Date()): boolean {
  const pause = toDate(sub?.pauseDate);
  if (!pause || pause > now) return false;
  const resume = toDate(sub?.resumeDate);
  return !resume || resume > now;
}

/**
 * Décide de l'accès d'un abonnement à un instant donné.
 * `null` (aucun abonnement) n'est PAS traité ici : l'appelant distingue « pas d'abonnement »
 * (compte sur droits manuels ou packs one-shot) de « abonnement terminé ».
 */
export function subscriptionAccess(
  sub: SubscriptionLike,
  now: Date = new Date(),
): SubscriptionAccess {
  const status = estUnEssaiEnCours(sub) ? "trial" : (sub.status || "").toLowerCase();
  const paused = isPauseActive(sub, now);
  const periodEnd = paidPeriodEnd(sub);
  const resumesAt = toDate(sub.resumeDate);

  // Actif ou essai, hors pause : ouvert sans condition de date.
  if (!paused && STATUTS_ACTIFS.has(status)) {
    return {
      isOpen: true,
      state: status === "trial" ? "trial" : "active",
      accessEndsAt: periodEnd,
      resumesAt: null,
      isPaused: false,
    };
  }

  const state: SubscriptionState = paused
    ? "paused"
    : status === "cancelled" || status === "cancelled_from_dunning"
      ? "cancelled"
      : status === "non_renewing"
        ? "non_renewing"
        : status === "unpaid" || status === "dunning" || status === "suspended"
          ? "unpaid"
          : "ended";

  // Un abonnement expiré reste fermé même si une pause traîne encore sur la fiche : la période
  // payée est consommée par définition. Sinon, l'accès court jusqu'à la fin de cette période.
  const peutCourir =
    !STATUTS_TERMINES.has(status) &&
    (paused || STATUTS_SORTIE.has(status) || STATUTS_ACTIFS.has(status));
  const encoreOuvert = peutCourir && periodEnd != null && periodEnd > now;

  // `state` dit l'ÉTAT RÉEL, `isOpen` dit l'accès : les deux sont indépendants. Un abonnement
  // en pause dont la période payée est écoulée reste « en pause » à l'écran — sinon le client
  // lirait « terminé » alors qu'une reprise est programmée.
  return {
    isOpen: encoreOuvert,
    // `accessEndsAt` est la fin de la période payée, qu'elle soit à venir (badge « accès
    // jusqu'au … ») ou passée (message « a pris fin le … »).
    accessEndsAt: periodEnd,
    state,
    resumesAt: paused ? resumesAt : null,
    isPaused: paused,
  };
}

/** Raccourci de lecture, pour les filtres d'écran et de requête. */
export function isSubscriptionOpen(sub: SubscriptionLike | null | undefined, now: Date = new Date()): boolean {
  return sub ? subscriptionAccess(sub, now).isOpen : false;
}

/**
 * Condition SQL équivalente à `subscriptionAccess().isOpen`, pour filtrer côté base sans
 * ramener toutes les lignes. Les deux doivent rester d'accord : toute évolution de la règle
 * se répercute ici. Les noms de colonnes sont ceux de la table `subscriptions`.
 */
const PAUSE_ACTIVE_SQL = `(pause_date IS NOT NULL AND pause_date <= NOW() AND (resume_date IS NULL OR resume_date > NOW()))`;

export const SUBSCRIPTION_OPEN_SQL = `(
  status NOT IN ('expired','trial_expired','creation_failed')
  AND (
    (status IN ('live','trial') AND NOT ${PAUSE_ACTIVE_SQL})
    OR (
      (status IN ('live','trial','cancelled','cancelled_from_dunning','non_renewing','unpaid','dunning','suspended') OR ${PAUSE_ACTIVE_SQL})
      AND COALESCE(current_term_ends_at, next_billing_at) > NOW()
    )
  )
)`;
